import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../shared/Modal'

/**
 * Cookie category configuration
 */
const COOKIE_CATEGORIES = {
  essential: {
    icon: '🔒',
    translationKey: 'essential',
    alwaysActive: true,
    providers: ['Session Management', 'Authentication', 'Security', 'Brevo Live Chat']
  },
  analytics: {
    icon: '📊',
    translationKey: 'analytics',
    alwaysActive: false,
    providers: ['Google Analytics (mit User-ID)']
  },
  sessionRecording: {
    icon: '🎥',
    translationKey: 'sessionRecording',
    alwaysActive: false,
    providers: ['Hotjar']
  },
  marketing: {
    icon: '📣',
    translationKey: 'marketing',
    alwaysActive: false,
    providers: ['Meta Pixel', 'Google Ads', 'Marketing Automation']
  }
}

/**
 * Toggle Switch Component
 */
const ToggleSwitch = ({ checked, onChange, disabled = false, id, 'aria-labelledby': ariaLabelledBy }) => {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        ${checked
          ? 'bg-gradient-to-r from-purple-600 to-pink-500'
          : 'bg-gray-300'
        }
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white
          transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-6' : 'translate-x-1'}
          ${!disabled && 'shadow-md'}
        `}
      />
    </button>
  )
}

/**
 * Cookie Preferences Modal Component
 * Allows users to manage their cookie preferences by category
 */
const CookiePreferencesModal = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation('cookies')
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    sessionRecording: false,
    marketing: false
  })
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // Load existing preferences if available
      const stored = localStorage.getItem('cookieConsent')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          setPreferences({
            essential: true, // Always true
            analytics: parsed.analytics || false,
            sessionRecording: parsed.sessionRecording || false,
            marketing: parsed.marketing || false
          })
        } catch (e) {
          console.error('Error parsing cookie preferences:', e)
        }
      }
      setHasChanges(false)
    }
  }, [isOpen])

  const handleToggle = (category, value) => {
    if (COOKIE_CATEGORIES[category].alwaysActive) return

    setPreferences(prev => ({
      ...prev,
      [category]: value
    }))
    setHasChanges(true)
  }

  const handleSave = () => {
    onSave(preferences)
    setHasChanges(false)
  }

  const handleRejectAll = () => {
    const newPreferences = {
      essential: true,
      analytics: false,
      sessionRecording: false,
      marketing: false
    }
    setPreferences(newPreferences)
    onSave(newPreferences)
  }

  const handleAcceptAll = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      sessionRecording: true,
      marketing: true
    }
    setPreferences(newPreferences)
    onSave(newPreferences)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      closeOnBackdrop={false}
      className="max-h-[90vh]"
    >
      {/* Modal Header */}
      <div className="border-b border-gray-200 px-6 py-4 -mx-6 -mt-4 mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          {t('preferences.title', 'Cookie Preferences')}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {t('preferences.subtitle', 'Manage your cookie preferences and choose which types of cookies you allow.')}
        </p>
      </div>

      {/* Cookie Categories */}
      <div className="space-y-6">
        {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => (
          <div
            key={key}
            className={`
              rounded-xl border p-5
              transition-all duration-200
              ${preferences[key]
                ? 'bg-purple-50/50 border-purple-200'
                : 'bg-gray-50 border-gray-200'
              }
            `}
          >
            {/* Category Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <span className="text-2xl mt-0.5" aria-hidden="true">{category.icon}</span>
                <div className="flex-1">
                  <h3
                    id={`cookie-category-${key}`}
                    className="font-semibold text-gray-900 flex items-center gap-2"
                  >
                    {t(`preferences.${category.translationKey}.title`)}
                    {category.alwaysActive && (
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                        {t('preferences.alwaysActive', 'Always Active')}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    {t(`preferences.${category.translationKey}.description`)}
                  </p>
                </div>
              </div>

              {/* Toggle Switch with ARIA label association */}
              <div className="ml-4">
                <ToggleSwitch
                  id={`cookie-${key}`}
                  aria-labelledby={`cookie-category-${key}`}
                  checked={preferences[key]}
                  onChange={(value) => handleToggle(key, value)}
                  disabled={category.alwaysActive}
                />
              </div>
            </div>

            {/* Providers List */}
            {preferences[key] && (
              <div className="mt-4 pt-4 border-t border-gray-200/50">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('preferences.providers', 'Providers')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {category.providers.map((provider) => (
                    <span
                      key={provider}
                      className="text-xs px-2.5 py-1 bg-white border border-gray-200 rounded-md text-gray-700"
                    >
                      {provider}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <span className="text-blue-600 mt-0.5" aria-hidden="true">ℹ️</span>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {t('preferences.info.title', 'About Cookies')}
            </p>
            <p className="text-xs leading-relaxed">
              {t('preferences.info.description',
                'Cookies are small text files that are placed on your device to help our website provide a better user experience. Essential cookies are required for the website to function properly.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-gray-200">
        <button
          onClick={handleRejectAll}
          className="
            flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm
            bg-gray-100 text-gray-700 border border-gray-300
            hover:bg-gray-200 hover:border-gray-400
            focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
            transition-all duration-200
          "
        >
          {t('preferences.rejectAll', 'Reject All')}
        </button>

        <button
          onClick={handleAcceptAll}
          className="
            flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm
            bg-white text-purple-600 border-2 border-purple-600
            hover:bg-purple-50 hover:border-purple-700
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
            transition-all duration-200
          "
        >
          {t('preferences.acceptAll', 'Accept All')}
        </button>

        <button
          onClick={handleSave}
          disabled={!hasChanges && preferences.analytics === false && preferences.marketing === false}
          className={`
            flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm
            bg-gradient-to-r from-purple-600 to-pink-500 text-white
            hover:from-purple-700 hover:to-pink-600
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
            transition-all duration-200
            shadow-lg shadow-purple-500/25
            transform hover:scale-105 active:scale-95
            ${(!hasChanges && preferences.analytics === false && preferences.marketing === false)
              ? 'opacity-50 cursor-not-allowed'
              : ''
            }
          `}
        >
          {t('preferences.savePreferences', 'Save Preferences')}
        </button>
      </div>
    </Modal>
  )
}

export default CookiePreferencesModal